import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
  ScrollView,
  AppState,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors, spacing, borderRadius, Icon, Alert } from '@tipsy/design-system';
import { useAuth } from 'auth/AuthContext';
import { userApi, stripeApi } from '../api';
import type { User, StripeAccount, ExternalAccount } from '../api';
import WebViewModal, { WebViewModalRef } from '../components/WebViewModal';

const ProfileScreen = () => {
  const { signOut, refreshOnboardingStatus } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>([]);
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const webViewModalRef = useRef<WebViewModalRef>(null);

  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true);
      const userData = await userApi.getMe();
      setUser(userData);
    } catch {
      Alert.alert('Error', 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchStripeAccount = useCallback(async () => {
    try {
      const account = await stripeApi.getAccount();
      setStripeAccount(account);

      // Fetch external accounts if Stripe account exists and is active
      if (account?.payoutsEnabled) {
        const accounts = await stripeApi.getExternalAccounts();
        setExternalAccounts(accounts);
      }
    } catch {
      // User may not have a Stripe account yet, that's OK
      setStripeAccount(null);
      setExternalAccounts([]);
    }
  }, []);

  const appStateRef = useRef(AppState.currentState);

  useEffect(() => {
    fetchUser();
    fetchStripeAccount();
  }, [fetchUser, fetchStripeAccount]);

  // Auto-refresh Stripe status when app becomes active (returning from browser)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', async (nextAppState) => {
      if (appStateRef.current.match(/inactive|background/) && nextAppState === 'active') {
        // App has come to foreground - refresh Stripe status
        try {
          const account = await stripeApi.syncAccountStatus();
          setStripeAccount(account);
          await refreshOnboardingStatus();

          // Refresh external accounts if account is active
          if (account?.payoutsEnabled) {
            const accounts = await stripeApi.getExternalAccounts();
            setExternalAccounts(accounts);
          }
        } catch {
          // Ignore errors on auto-refresh
        }
      }
      appStateRef.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [refreshOnboardingStatus]);

  const handleSignOut = async () => {
    Alert.alert('Sign Out', 'Are you sure you want to sign out?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Sign Out',
        style: 'destructive',
        onPress: async () => {
          setIsLoading(true);
          try {
            await signOut();
          } catch {
            Alert.alert('Error', 'Failed to sign out. Please try again.');
          } finally {
            setIsLoading(false);
          }
        },
      },
    ]);
  };

  const handleSetupPayouts = async () => {
    try {
      setIsStripeLoading(true);

      // Create account if not exists
      let account = stripeAccount;
      if (!account) {
        account = await stripeApi.createAccount();
        setStripeAccount(account);
      }

      // Get onboarding link
      const { url } = await stripeApi.createAccountLink();

      // Open in WebView modal
      webViewModalRef.current?.open(url);
    } catch {
      Alert.alert('Error', 'Failed to setup payouts. Please try again.');
    } finally {
      setIsStripeLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      setIsStripeLoading(true);
      const { url } = await stripeApi.getDashboardLink();
      webViewModalRef.current?.open(url);
    } catch {
      Alert.alert('Error', 'Failed to open dashboard. Please try again.');
    } finally {
      setIsStripeLoading(false);
    }
  };

  const handleWebViewClose = useCallback(async () => {
    // Refresh Stripe status when modal closes
    try {
      const account = await stripeApi.syncAccountStatus();
      setStripeAccount(account);
      await refreshOnboardingStatus();

      if (account?.payoutsEnabled) {
        const accounts = await stripeApi.getExternalAccounts();
        setExternalAccounts(accounts);
      }
    } catch {
      // Ignore errors on refresh
    }
  }, [refreshOnboardingStatus]);

  const handleRefreshStripeStatus = async () => {
    try {
      setIsStripeLoading(true);
      const account = await stripeApi.syncAccountStatus();
      setStripeAccount(account);
      // Refresh onboarding status in auth provider
      await refreshOnboardingStatus();
    } catch {
      Alert.alert('Error', 'Failed to refresh status');
    } finally {
      setIsStripeLoading(false);
    }
  };

  const getStripeStatusText = () => {
    if (!stripeAccount) return null;

    if (stripeAccount.payoutsEnabled) {
      return { text: 'Active', color: colors.status.success };
    }
    if (stripeAccount.detailsSubmitted) {
      return { text: 'Pending verification', color: colors.status.warning };
    }
    return { text: 'Setup incomplete', color: colors.status.error };
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const isOnboarded = stripeAccount?.payoutsEnabled;

  const getPaymentMethodIcon = (account: ExternalAccount) => {
    if (account.object === 'card') {
      return 'credit-card';
    }
    return 'account-balance';
  };

  const getPaymentMethodTitle = (account: ExternalAccount) => {
    if (account.object === 'card') {
      return `${account.brand || 'Card'} •••• ${account.last4}`;
    }
    return `${account.bankName || 'Bank'} •••• ${account.last4}`;
  };

  if (isLoading && !user) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.interactive.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Avatar Section */}
        <View style={styles.avatarSection}>
          {user?.avatar ? (
            <Image source={{ uri: user.avatar }} style={styles.avatarImage} />
          ) : (
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{getInitials()}</Text>
            </View>
          )}
        </View>

        {/* Welcome message for users who need to setup payouts */}
        {!isOnboarded && (
          <View style={styles.welcomeSection}>
            <Text style={styles.welcomeTitle}>Welcome to Tipsy!</Text>
            <Text style={styles.welcomeText}>
              To start receiving tips, you need to set up your payout account. This only takes a few
              minutes.
            </Text>
          </View>
        )}

        {/* Payout Settings Section */}
        <View style={styles.profileSection}>
          <Text style={styles.sectionTitle}>Payout Settings</Text>

          {stripeAccount ? (
            <>
              <View style={styles.fieldContainer}>
                <Text style={styles.fieldLabel}>Status</Text>
                <View style={styles.statusRow}>
                  <View
                    style={[styles.statusBadge, { backgroundColor: getStripeStatusText()?.color }]}
                  >
                    <Text style={styles.statusBadgeText}>{getStripeStatusText()?.text}</Text>
                  </View>
                  <TouchableOpacity
                    onPress={handleRefreshStripeStatus}
                    disabled={isStripeLoading}
                    style={styles.refreshButton}
                  >
                    <Text style={styles.refreshButtonText}>Refresh</Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={[styles.fieldContainer, styles.fieldContainerLast]}>
                {stripeAccount.payoutsEnabled ? (
                  <TouchableOpacity
                    style={styles.stripeButton}
                    onPress={handleOpenDashboard}
                    disabled={isStripeLoading}
                  >
                    {isStripeLoading ? (
                      <ActivityIndicator color={colors.text.inverse} />
                    ) : (
                      <Text style={styles.stripeButtonText}>Open Dashboard</Text>
                    )}
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={styles.stripeButton}
                    onPress={handleSetupPayouts}
                    disabled={isStripeLoading}
                  >
                    {isStripeLoading ? (
                      <ActivityIndicator color={colors.text.inverse} />
                    ) : (
                      <Text style={styles.stripeButtonText}>Complete Setup</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            </>
          ) : (
            <View style={[styles.fieldContainer, styles.fieldContainerLast]}>
              <TouchableOpacity
                style={styles.stripeButton}
                onPress={handleSetupPayouts}
                disabled={isStripeLoading}
              >
                {isStripeLoading ? (
                  <ActivityIndicator color={colors.text.inverse} />
                ) : (
                  <Text style={styles.stripeButtonText}>Setup Payouts</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </View>

        {isOnboarded && (
          <View style={styles.profileSection}>
            <Text style={styles.sectionTitle}>Payout methods</Text>

            {externalAccounts.map((account) => (
              <View key={account.id} style={styles.paymentMethodItem}>
                <View style={styles.paymentMethodInfo}>
                  <Icon
                    name={getPaymentMethodIcon(account)}
                    size={24}
                    color={colors.text.secondary}
                    style={styles.paymentMethodIcon}
                  />
                  <View style={styles.paymentMethodDetails}>
                    <Text style={styles.paymentMethodTitle}>{getPaymentMethodTitle(account)}</Text>
                    <Text style={styles.paymentMethodSubtitle}>
                      {account.currency.toUpperCase()}
                      {account.defaultForCurrency && ' • Default'}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        <TouchableOpacity style={styles.signOutButton} onPress={handleSignOut} disabled={isLoading}>
          {isLoading ? (
            <ActivityIndicator color={colors.status.error} />
          ) : (
            <Text style={styles.signOutText}>Sign Out</Text>
          )}
        </TouchableOpacity>
      </ScrollView>

      <WebViewModal ref={webViewModalRef} onClose={handleWebViewClose} />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flex: 1,
    paddingHorizontal: spacing.base,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarSection: {
    alignItems: 'center',
    paddingVertical: spacing['3xl'],
  },
  avatar: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: colors.interactive.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  avatarText: {
    fontSize: 40,
    fontWeight: '600',
    color: colors.text.inverse,
  },
  welcomeSection: {
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.xl,
  },
  welcomeTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  welcomeText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 24,
  },
  profileSection: {
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.xl,
    paddingVertical: spacing.sm,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.sm,
  },
  fieldContainer: {
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.light,
  },
  fieldContainerLast: {
    borderBottomWidth: 0,
  },
  fieldLabel: {
    fontSize: 13,
    color: colors.text.tertiary,
    marginBottom: spacing.xs,
  },
  signOutButton: {
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginBottom: spacing['3xl'],
  },
  signOutText: {
    fontSize: 17,
    color: colors.status.error,
    fontWeight: '500',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  statusBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.inverse,
  },
  refreshButton: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  refreshButtonText: {
    fontSize: 15,
    color: colors.interactive.primary,
    fontWeight: '500',
  },
  stripeButton: {
    backgroundColor: colors.interactive.primary,
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  stripeButtonText: {
    fontSize: 17,
    color: colors.text.inverse,
    fontWeight: '500',
  },
  paymentMethodItem: {
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.light,
  },
  paymentMethodInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  paymentMethodIcon: {
    marginRight: spacing.md,
  },
  paymentMethodDetails: {
    flex: 1,
  },
  paymentMethodTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text.primary,
  },
  paymentMethodSubtitle: {
    fontSize: 14,
    color: colors.text.secondary,
    marginTop: spacing.xs,
  },
  manageButton: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  manageButtonText: {
    fontSize: 16,
    color: colors.interactive.primary,
    fontWeight: '500',
  },
  emptyStateText: {
    fontSize: 15,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: spacing.md,
    lineHeight: 22,
  },
});

export default ProfileScreen;
